import { UAParser } from 'ua-parser-js';

const config = {
    onError: (error) => window.console.error(error),
};
const Globals = {
    window: () => window,
    document: () => document,
    body: () => document.body,
    navigator: () => navigator,
    config: () => config,
};

const first = (objOrArray, predicate) => {
    for (const key in objOrArray) {
        // eslint-disable-next-line
        if (objOrArray.hasOwnProperty(key)) {
            const value = objOrArray[key];
            const fulfilled = predicate(value, key);
            if (fulfilled)
                return value;
        }
    }
};
const forEach = (objOrArray, mapper) => first(objOrArray, (v, k) => {
    mapper(v, k);
});
const filter = (array, predicate) => {
    const result = [];
    forEach(array, v => {
        if (predicate(v))
            result.push(v);
    });
    return result;
};
const classNamesFilter = (arg) => !!arg && typeof arg === 'string';
const classNames = (...classes) => classes.filter(classNamesFilter).join(' ') || undefined;
/**
 * Produce a random string of Hex digits of a certain length.
 *
 * NOTE: this random string is not cryptographically secure!
 * @param length
 */
const randomHexString = (length) => {
    const result = [];
    for (let i = 0; i < length; i++) {
        result.push(Math.floor(Math.random() * 16).toString(16));
    }
    return result.join('');
};
/**
 * This is a wrapper for promise returning functions, which ensures that those will
 * not be performed in parallel.
 *
 * If it is called a second time when there is already a request in progress (promise is unresolved),
 * the same promise is returned again and func is not called.
 */
const onlyOneInParallel = (func) => {
    let pending = undefined;
    return async (arg) => {
        if (pending)
            return pending;
        try {
            const result = await (pending = func(arg));
            pending = undefined; // eslint-disable-line
            return result;
        }
        catch (e) {
            // we have to set the pending promise to undefined again, but simply rethrow the occurred error
            pending = undefined;
            throw e;
        }
    };
};
/**
 * This is a wrapper for promise returning functions, which ensures that previously made
 * and still pending requests will be skipped in favor to the latest made request.
 *
 * Somewhat the opposite of "onlyOneInParallel" (see above).
 */
const replacePending = (func) => {
    let counter = 0;
    let lastPending = undefined;
    return (arg) => {
        const tag = ++counter; // hereby the latest incoming function call will be kind of tagged
        lastPending = new Promise((resolve, reject) => {
            func(arg)
                .then(result => {
                // success: subscribe only to the tagged function call
                if (tag === counter)
                    resolve(result);
                else
                    return resolve(lastPending);
            })
                .catch(error => {
                // error: subscribe only to the tagged function call
                if (tag === counter)
                    reject(error);
                else
                    return resolve(lastPending);
            });
        });
        return lastPending;
    };
};
/**
 * This is a wrapper for promise returning functions ensures that all calls
 * are processed sequentially and parallel invocations are avoided.
 */
const enqueueAsync = (func) => {
    let pending = Promise.resolve();
    return (arg) => (pending = pending.catch(() => { }).then(() => func(arg)));
};
const debounced = (func, delay) => {
    let lastPending = 0;
    return (...args) => {
        Globals.window().clearTimeout(lastPending);
        lastPending = Globals.window().setTimeout(() => func(...args), delay);
    };
};
const sleep = (ms) => new Promise(resolve => Globals.window().setTimeout(resolve, ms));
const uuid = (a) => a
    ? (a ^ ((Math.random() * 16) >> (a / 4))).toString(16)
    : ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, uuid);
const configure = ({ onError }) => {
    onError && (Globals.config().onError = onError);
};
const waitFor = async (callback, delay = 300) => {
    while (!callback()) {
        await sleep(delay);
    }
};
const base64ToArrayBuffer = (base64) => {
    const binaryString = Globals.window().atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
};
const createBlob = async (base64, dataType) => {
    const arraybuffer = base64ToArrayBuffer(base64);
    const blob = new Blob([arraybuffer], dataType);
    const url = Globals.window().URL.createObjectURL(blob);
    return [blob, url];
};
const _ = {
    classNames,
    first,
    forEach,
    filter,
    debounced,
    randomHexString,
    onlyOneInParallel,
    replacePending,
    enqueueAsync,
    sleep,
    uuid,
    configure,
    waitFor,
    base64ToArrayBuffer,
    createBlob,
};

/**
 * Wording conventions:
 * 1) search string => Part of the url preceded by the pathname.
 *      e.g.: ?uuid=whatever&page=4
 * 2) query string => search string without "?"
 *      e.g.: uuid=whatever&page=4
 * 3) query => the associated object of the query string
 *      e.g.: {uuid: 'whatever', page: '4'}
 *
 * ATTENTION: All values will be strings!!!
 */
/**
 * Decodes query params and parses both "%20" and "+" as spaces
 */
const decodeQueryParam = (param) => decodeURIComponent(param.replace(/\+/g, ' '));
/**
 * Transforms location search string into query object.
 */
const locationSearchToQuery = (search = '', allowParameterDoubles = false) => {
    const s = search.startsWith('?') ? search.substr(1) : search; // hash history not considered for now
    const params = s.split('&');
    const result = {};
    for (let i = 0; i < params.length; i++) {
        const param = params[i];
        if (param) {
            const [key, value] = param.split('=');
            const possibleDoubleKeyValue = result[decodeQueryParam(key)];
            if (possibleDoubleKeyValue !== undefined && value !== undefined && allowParameterDoubles) {
                const prevValues = Array.isArray(possibleDoubleKeyValue)
                    ? possibleDoubleKeyValue
                    : [possibleDoubleKeyValue];
                result[decodeQueryParam(key)] = [...prevValues, decodeQueryParam(value)];
            }
            else if (value !== undefined)
                result[decodeQueryParam(key)] = decodeQueryParam(value);
        }
    }
    return result;
};
/**
 * Transforms location search string into a query object with arrays as value:
 * '?key=value1&key=value2' -> { key: ['value1', 'value2'] }
 */
const locationSearchToQueryAllValues = (search = '') => {
    const s = search.startsWith('?') ? search.substr(1) : search;
    const params = s.split('&');
    const result = {};
    for (let i = 0; i < params.length; i++) {
        const param = params[i];
        if (param) {
            const pair = param.split('=');
            const key = pair[0];
            const value = pair[1];
            if (key && value) {
                const decodedKey = decodeQueryParam(key);
                const decodedValue = decodeQueryParam(value);
                result[decodedKey] = result[decodedKey] ? [...result[decodedKey], decodedValue] : [decodedValue];
            }
        }
    }
    return result;
};
const constructQueryPair = (key, value) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
/**
 * Transforms query object to query string.
 */
const queryToString = (query, allowDoubles = false) => Object.keys(query)
    .map(key => {
    const value = query[key];
    if (value !== undefined && value !== null) {
        if (Array.isArray(value) && allowDoubles) {
            // { key: ['a', 'b']} -> 'key=a&key=b'
            return value
                .map(doubleValue => constructQueryPair(key, doubleValue))
                .filter(Boolean)
                .join('&');
        }
        // { key: 'a' } -> 'key=a' OR (if an array) { key: ['a', 'b'] } -> 'key=a,b'
        return constructQueryPair(key, value);
    }
})
    .filter(Boolean)
    .join('&');
/**
 * Transforms query object to search string.
 */
const queryToSearch = (query, allowDoubles = false) => {
    const queryString = queryToString(query, allowDoubles);
    return queryString ? `?${queryString}` : '';
};
/**
 * Calculates the search after merging all given query updates into
 * the current URL.
 */
const updateSearch = (query, pathname = Globals.window().location.pathname, allowDoubles = false) => pathname +
    UrlUtil.queryToSearch({
        ...UrlUtil.locationSearchToQuery(Globals.window().location.search, allowDoubles),
        ...query,
    }, allowDoubles);
/**
 * Return a cover image from a cover image path relative to the media server base URL
 */
const coverUri = (mediaServiceBaseUrl, coverPath) => mediaServiceBaseUrl + '/coverscans/' + coverPath;
/**
 * Returns an unique random string to be used as nonce parameter
 */
const getNonce = () => {
    let result = '';
    for (let i = 0; i < 10; i++) {
        result += ((Math.random() * 16) | 0).toString(16);
    }
    return result;
};
const toRelativeUrl = (absoluteUrl) => {
    try {
        const url = new URL(absoluteUrl);
        const relativeUrl = url.pathname + url.search + url.hash;
        return relativeUrl;
    }
    catch (error) {
        return absoluteUrl;
    }
};
const UrlUtil = {
    decodeQueryParam,
    queryToString,
    locationSearchToQuery,
    locationSearchToQueryAllValues,
    queryToSearch,
    coverUri,
    getNonce,
    updateSearch,
    toRelativeUrl,
};

const uaParser = new UAParser();
// TODO this implementation should be reviewed. (MW)
const ANDROID = /android ([0-9]+\.[0-9]+)/;
const E_INK_TOLINO = /tolino (shine|vision|page|epos)/;
const E_INK_SONY = /ebrd[0-9]+/;
const OUTDATED_BROWSER = /msie\s([2-9]|10)/;
// This might not be true. E.g. Ipad Devices might not have "ipad" included in their UA. (MW)
const IOS_DEVICE = /iphone|ipad/;
const _UserAgent = {
    parse: () => {
        const userAgent = Globals.navigator().userAgent.toLowerCase();
        // determine android version
        const match = ANDROID.exec(userAgent);
        const androidNum = match ? Number(match[1]) : NaN;
        const android = isNaN(androidNum) ? undefined : androidNum;
        // determine if eInk is present
        const isSonyReader = E_INK_SONY.test(userAgent);
        const eInk = E_INK_TOLINO.test(userAgent) || isSonyReader;
        const tolino = E_INK_TOLINO.test(userAgent);
        // determine if the device has outdated CSS skills
        // this is true for the Tolino Shine 1, which we still need to support, or other outdated devices
        const outdatedCSS = (!!android && android < 3) || isSonyReader;
        // determine if the device can handle PayPal payments
        const payPalPossible = !isSonyReader && (!android || android > 4.3);
        const outdatedBrowser = OUTDATED_BROWSER.test(userAgent);
        const isIosDevice = IOS_DEVICE.test(userAgent);
        return {
            android,
            eInk,
            tolino,
            outdatedCSS,
            payPalPossible,
            outdatedBrowser,
            isSonyReader,
            isIosDevice,
            uaParser,
        };
    },
};
const UserAgent = _UserAgent.parse();

const isMobile = () => ('ontouchstart' in document.documentElement && window.matchMedia('(any-pointer:coarse)').matches) ||
    UserAgent.uaParser.getDevice()?.type === 'mobile';
const DeviceDetector = { isMobile };

const _ScrollUtil = {};
const SCROLL_INTO_VIEW_OFFSET = 150;
const SCROLL_FREQUENCY = 20;
_ScrollUtil.disabled = !('pageYOffset' in Globals.window());
_ScrollUtil.animated = !UserAgent.eInk;
_ScrollUtil.node = Globals.window(); // we use the window as default node to scroll on
/**
 * Initially we used "document.documentElement.scrollTop" or "document.body.scrollTop" to determine the
 * scroll offset. This does not work for neither Tolino nor iOS devices.
 *
 * Since then we are using "window.pageYOffset", which works perfectly fine for all devices.
 *
 * When the ScrollUtil was mounted on an scrollable HTMLElement, then we have to fallback onto "scrollTop"
 * since "pageYOffset" is only defined on the window.
 */
const getOffset = () => _ScrollUtil.node.pageYOffset || _ScrollUtil.node.scrollTop || 0;
// ATTENTION: Falling back to body.scrollHeight if no node was mounted (since window.scrollHeight === undefined)
const getTotalHeight = () => _ScrollUtil.node.scrollHeight || Globals.body().scrollHeight;
const scrollBy = (pos) => _ScrollUtil.node.scrollBy(0, pos);
const scrollTo = (pos) => _ScrollUtil.node.scrollTo(0, pos);
_ScrollUtil.mount = node => {
    // for outdated devices certain elements (like the modal) have no inner scroll container
    // but scroll on the window itself. Hence we should operate at all times only on the window.
    if (!UserAgent.outdatedCSS) {
        _ScrollUtil.node = node || Globals.window();
    }
};
const mount = (node) => _ScrollUtil.mount(node);
const unmount = () => _ScrollUtil.mount();
_ScrollUtil.smoothScrollTo = (pos, oldRealDist) => {
    //use the modern version of scrolling
    if (!UserAgent.eInk) {
        _ScrollUtil.node.scrollTo({
            top: pos,
            behavior: 'smooth',
        });
    }
    else {
        // the distance of our current scroll offset to the desired scroll position
        const realDist = pos - ScrollUtil.getOffset();
        const roundedDist = Math.floor(realDist);
        // if the distance did not change in comparison to the previous distance, the scrolling has no effect anymore
        // e.g. we have reached the bottom or the top of the page
        // additionally we will stop to scroll if the distance is less than 1 pixel
        if (realDist !== oldRealDist && Math.abs(roundedDist) > 1) {
            ScrollUtil.scrollBy(roundedDist / 8);
            _.sleep(SCROLL_FREQUENCY).then(() => _ScrollUtil.smoothScrollTo(pos, realDist));
        }
    }
};
const smoothScrollTo = (pos, oldRealDist) => {
    if (_ScrollUtil.disabled)
        return;
    _ScrollUtil.animated ? _ScrollUtil.smoothScrollTo(pos, oldRealDist) : ScrollUtil.scrollTo(pos);
};
_ScrollUtil.getDesiredScrollPos = element => {
    const desiredScrollPos = ScrollUtil.getOffset() + element.getBoundingClientRect().top - SCROLL_INTO_VIEW_OFFSET;
    return Math.max(desiredScrollPos, 0);
};
const smoothScrollIntoView = (element) => {
    if (_ScrollUtil.disabled)
        return;
    const desiredScrollPos = _ScrollUtil.getDesiredScrollPos(element);
    _ScrollUtil.animated ? _ScrollUtil.smoothScrollTo(desiredScrollPos) : ScrollUtil.scrollTo(desiredScrollPos);
};
_ScrollUtil.instantScrollTo = (pos) => _ScrollUtil.node.scrollTo({
    top: pos,
    behavior: 'instant',
});
// Since our standard scroll behaviour is now defined smooth via CSS, we need to explicitly scroll instantly, if needed.
const instantScrollTo = (pos) => {
    if (_ScrollUtil.disabled)
        return;
    _ScrollUtil.instantScrollTo(pos);
};
const isWindowMounted = () => _ScrollUtil.node === Globals.window();
const ScrollUtil = {
    smoothScrollIntoView,
    smoothScrollTo,
    mount,
    unmount,
    getOffset,
    getTotalHeight,
    scrollTo,
    scrollBy,
    instantScrollTo,
    isWindowMounted,
};

const _DomUtil = {};
/**
 * Iterates with a given matcher over the element and its parents
 * to determine if the target is inside of an element that matches
 * the specified conditions.
 *
 * Iteration limit is 100 elements.
 */
_DomUtil.containedInMatchingElement =
    matcher => (target, ...args) => {
        let current = target;
        for (let i = 0; current && i < 100; i++) {
            if (matcher(current, ...args))
                return true;
            current = current.parentElement;
        }
        return false;
    };
_DomUtil.containsClassNames = (className, ...searched) => searched.filter(clazz => (className || '').split(' ').indexOf(clazz) === -1).length === 0;
_DomUtil.matchesElement = (target, element) => target === element;
_DomUtil.matchesId = (target, id) => !!target && target.id === id;
_DomUtil.matchesSelectedClassNames = (target, ...classNames) => !!target && _DomUtil.containsClassNames(target.className, ...classNames);
const containedInElement = _DomUtil.containedInMatchingElement(_DomUtil.matchesElement);
const containedInElementWithId = _DomUtil.containedInMatchingElement(_DomUtil.matchesId);
const containedInElementWithClassNames = _DomUtil.containedInMatchingElement(_DomUtil.matchesSelectedClassNames);
_DomUtil.scrollToElement = element => ScrollUtil.smoothScrollIntoView(element);
_DomUtil.scrollToClass = (className, logErrorIfMultiple) => {
    const elements = Globals.document().getElementsByClassName(className);
    if (logErrorIfMultiple && elements.length > 1)
        Globals.config().onError(`scrollToUniqueClass: found multiple elements for class name '${className}'!`);
    const element = elements[0];
    element && _DomUtil.scrollToElement(element);
};
const scrollToClass = (className) => {
    _DomUtil.scrollToClass(className, false);
};
const scrollToUniqueClass = (className) => {
    _DomUtil.scrollToClass(className, true);
};
_DomUtil.scrollToId = (id, logErrorIfMissing) => {
    const element = Globals.document().getElementById(id);
    if (element)
        _DomUtil.scrollToElement(element);
    else if (logErrorIfMissing)
        Globals.config().onError(`scrollToId: no element found for id '${id}'!`);
};
const tryScrollToId = (id) => _DomUtil.scrollToId(id, false);
const scrollToId = (id) => _DomUtil.scrollToId(id, true);
const addBodyClass = (className) => {
    const currentClasses = Globals.body().className;
    if (!currentClasses) {
        Globals.body().className = className;
        return;
    }
    const classes = currentClasses.split(/\s+/);
    if (classes.indexOf(className) < 0) {
        classes.push(className);
        Globals.body().className = classes.join(' ');
    }
};
const removeBodyClass = (className) => {
    const currentClasses = Globals.body().className || '';
    const classes = currentClasses.split(/\s+/);
    if (classes.indexOf(className) >= 0) {
        Globals.body().className = classes.filter(clazz => clazz !== className).join(' ');
    }
};
const DomUtil = {
    containedInElement,
    containedInElementWithId,
    containedInElementWithClassNames,
    scrollToClass,
    scrollToUniqueClass,
    scrollToId,
    tryScrollToId,
    addBodyClass,
    removeBodyClass,
};

const isInteger = (num) => !isNaN(num) && num % 1 === 0;
const _castToNum = (arg) => (arg === null ? NaN : +arg);
const getInteger = (arg, fallback) => {
    const num = _castToNum(arg);
    return isInteger(num) ? num : fallback;
};
const getPositiveInteger = (arg, fallback) => {
    const num = _castToNum(arg);
    return isInteger(num) && num > 0 ? num : fallback;
};
const NumberUtil = { isInteger, getInteger, getPositiveInteger };

const _DateUtil = {};
_DateUtil.parseFallback = value => {
    // this is how our api date string looks like
    const match = value.match(/([0-9]{4})-([0-9]{2})-([0-9]{2})[ T]([0-9]{2}):([0-9]{2}):([0-9]{2}).*/);
    if (match) {
        // extract information
        const year = parseInt(match[1]);
        const month = parseInt(match[2]) - 1;
        const day = parseInt(match[3]);
        const hours = parseInt(match[4]);
        const minutes = parseInt(match[5]);
        const seconds = parseInt(match[6]);
        // create a date
        const parsed = new Date(year, month, day, hours, minutes, seconds);
        // validate that the created date matches the date string
        const dateIsValid = year === parsed.getFullYear() && month === parsed.getMonth() && day === parsed.getDate();
        return dateIsValid ? parsed : undefined;
    }
    else {
        return undefined;
    }
};
/**
 * WARNING: Always use this function to parse from the WSAPI! Because `new Date()` can't parse the WSAPI format
 * on the Internet Explorer.
 */
const parse = (value) => {
    const parsedDate = new Date(value);
    // if the date parsing returns an invalid date, try our fallback date parsing
    return !isNaN(parsedDate.getFullYear()) ? parsedDate : _DateUtil.parseFallback(value);
};
const _withLeadingZero = (num) => ('0' + num).slice(-2);
const toWsApiString = (date) => `${date.getFullYear()}-${_withLeadingZero(date.getMonth() + 1)}-${_withLeadingZero(date.getDate())}`;
const toGermanString = (date) => `${_withLeadingZero(date.getDate())}.${_withLeadingZero(date.getMonth() + 1)}.${date.getFullYear()}`;
/**
 * e.g. "Do., 20.12."
 */
const toGermanDateString = (date, opts) => {
    const defaultOpts = {
        weekday: opts?.omitWeekday ? undefined : 'short',
        day: '2-digit',
        month: opts?.monthFormat ?? '2-digit',
    };
    if (opts?.showYear) {
        defaultOpts.year = opts?.yearFormat ?? '2-digit';
    }
    let dateString = date.toLocaleDateString('de-DE', defaultOpts);
    if (defaultOpts.weekday && opts?.stripWeekdayDecimal) {
        dateString = dateString.replace('.', '');
    }
    return dateString;
};
const sameYearDates = (date1, date2) => date1.getFullYear() === date2.getFullYear();
const getFormattedDateRange = (date1, date2, opts) => {
    const showYear = opts?.showYear || !sameYearDates(date1, date2);
    const formattedDate1 = toGermanDateString(date1, { stripWeekdayDecimal: true, omitWeekday: opts?.omitWeekday });
    const formattedDate2 = toGermanDateString(date2, {
        stripWeekdayDecimal: true,
        showYear,
        yearFormat: opts?.yearFormat,
        omitWeekday: opts?.omitWeekday,
    });
    if (opts?.asArray) {
        return [formattedDate1, formattedDate2];
    }
    return `${formattedDate1} - ${formattedDate2}`;
};
const equalDates = (date1, date2) => {
    const formattedDate1 = toGermanDateString(date1);
    const formattedDate2 = toGermanDateString(date2);
    return formattedDate1 === formattedDate2;
};
const getFormattedTime = (date, showSuffix = true) => {
    if (!date) {
        return null;
    }
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const suffix = showSuffix ? ' Uhr' : '';
    return `${hours}:${minutes}${suffix}`;
};
const getFormattedTimeRange = (start, end) => {
    const showSuffixOnOnlyStartDate = !end;
    const startTime = getFormattedTime(start, showSuffixOnOnlyStartDate);
    const endTime = getFormattedTime(end);
    return [startTime, endTime].join(' - ');
};
const DateUtil = {
    parse,
    toWsApiString,
    toGermanString,
    toGermanDateString,
    getFormattedDateRange,
    equalDates,
    sameYearDates,
    getFormattedTime,
    getFormattedTimeRange,
};

const _OutdatedBrowser = {
    fallBackHtml: '<h1>Ihr Browser wird nicht unterstützt. Bitte verwenden Sie eine aktuelle Version.</h1>',
};
const showFallback = () => {
    if (UserAgent.outdatedBrowser) {
        const root = Globals.document().getElementById('root');
        if (root) {
            root.innerHTML = _OutdatedBrowser.fallBackHtml;
            return true;
        }
    }
    return false;
};
const OutdatedBrowser = { showFallback };

/**
 * Redirect to the given URI with optional query.
 * @param baseUri The base URI, i.e. an URI without any query components
 * @param queryParams Optional query to append to the base URI
 * @param withHistory Optional flag for redirect without the possibility to navigate back
 */
const redirect = (baseUri, queryParams, withHistory) => {
    const query = queryParams !== undefined ? UrlUtil.queryToSearch(queryParams) : '';
    const targetUri = baseUri + query;
    if (withHistory) {
        Globals.window().location.assign(targetUri);
    }
    else {
        Globals.window().location.replace(targetUri);
    }
};
/**
 * Reload the the same URI.
 */
const reload = () => {
    Globals.window().location.reload();
};
const LocationUtil = { redirect, reload };

/**
 * Calculates the discount in percent.
 * @param price represents the current price
 * @param oldPrice represents the old price
 */
const calculate = (price, oldPrice) => {
    if (price < oldPrice)
        return Math.round(((oldPrice - price) * 100) / oldPrice);
};
const DiscountUtil = { calculate };

const isNumberSearch = (searchTerm) => {
    // match isbn[:] or ISBN[:]
    const isbnRegex = new RegExp(/isbn:?/gi);
    // match digits and soft/hard separators
    const numberRegex = new RegExp(/^[-0-9xX,;. \t\r\n]+$/);
    // remove isbn string
    const noIsbnLiteral = searchTerm.replace(isbnRegex, '');
    // check that the rest is a sequence of digits and soft/hard separators with length > 8
    return noIsbnLiteral.length > 8 && numberRegex.test(noIsbnLiteral);
};
const getSearchNumbers = (rawSearchString) => {
    // if it is not a number search then don't parse the string
    if (!isNumberSearch(rawSearchString))
        return [];
    // remove all hyphens
    const noHyphens = rawSearchString.replace(/[-—_–]/g, '');
    // X can only be the last digit of an ISBN-10, so insert a hard separator.
    const searchString = noHyphens.replace(/[xX]/g, 'X,');
    // split string on separator to get the array of numbers
    const arrayOfNumbers = searchString.split(/ *[^0-9xX ]+[^0-9xX]*| +/).filter(str => !!str);
    return arrayOfNumbers;
};
const NumberSearchUtil = { isNumberSearch, getSearchNumbers };

var appPaths;
(function (appPaths) {
    appPaths["CHECKOUT"] = "/checkout";
    appPaths["CUSTOMER"] = "/de/account";
    appPaths["SEARCH_RESULTS"] = "/de/search";
})(appPaths || (appPaths = {}));
const matchesCurrentLocation = (withApp) => Globals.window().location.pathname.includes(withApp);
const AppPath = { matchesCurrentLocation, appPaths };

const getFormattedPrice = (priceCent) => (priceCent / 100).toFixed(2).replace('.', ',') + ' €';
const PriceUtil = {
    getFormattedPrice,
};

export { AppPath, DateUtil, DeviceDetector, DiscountUtil, DomUtil, LocationUtil, NumberSearchUtil, NumberUtil, OutdatedBrowser, PriceUtil, ScrollUtil, UrlUtil, UserAgent, _ };
